import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ClipboardCheck, FileSignature, FileText, KeyRound, Link2Off, LogIn, MessageSquareText } from "lucide-react";

import BrandLockup from "../components/brand/BrandLockup";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";

/** Landing page for the personal sign-in link in an email.
 *
 * Opening the page changes nothing (mail scanners open links too). The
 * person presses the button, which signs them in and opens the page the
 * email was about. Someone who has not chosen a PIN yet is then asked to
 * choose one (PinChangeOverlay, without the current PIN).
 */
type LinkInfo = {
  status: "ok" | "used" | "expired" | "invalid" | "inactive";
  first_name?: string;
  email?: string;
  needs_pin?: boolean;
  kind?: "documents" | "appraisal" | "feedback" | "job_description" | "general";
};

const KIND_ICON = {
  documents: FileText,
  appraisal: ClipboardCheck,
  feedback: MessageSquareText,
  job_description: FileSignature,
  general: LogIn,
};

export default function LinkSignInPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { onLoggedIn } = useAuth();
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = async () => {
    try {
      setInfo(await api.get<LinkInfo>(`/api/auth/link/${encodeURIComponent(token)}`));
    } catch {
      setInfo({ status: "invalid" });
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const go = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const res = await api.post<{ next: string }>(
        `/api/auth/link/${encodeURIComponent(token)}`,
        {},
      );
      await onLoggedIn();
      navigate(res.next || "/home", { replace: true });
    } catch {
      setFailed(true);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const toSignIn = () => navigate("/", { state: { email: info?.email ?? "" } });

  let body: React.ReactNode = (
    <p className="text-center text-sm text-gray-500">{t("common.loading")}</p>
  );

  if (info?.status === "ok") {
    const kind = info.kind ?? "documents";
    const Icon = KIND_ICON[kind] ?? FileText;
    const bodyText =
      kind === "documents"
        ? info.needs_pin ? t("link.okBodyFirst") : t("link.okBody")
        : t(info.needs_pin ? `link.${kind}.bodyFirst` : `link.${kind}.body`);
    const buttonText = kind === "documents" ? t("link.continue") : t(`link.${kind}.continue`);
    body = (
      <div className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-pme-blue/10 text-pme-blue">
          <Icon size={26} strokeWidth={1.75} aria-hidden />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">
            {t("link.hello", { name: info.first_name ?? "" })}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {bodyText}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void go()}
          disabled={busy}
          className="block w-full rounded-lg bg-pme-blue py-3 text-base font-semibold text-white shadow transition active:scale-[0.99] disabled:opacity-60"
        >
          {busy ? t("common.loading") : buttonText}
        </button>
        {failed && (
          <p role="alert" className="text-sm text-red-600">
            {t("link.failed")}
          </p>
        )}
      </div>
    );
  } else if (info) {
    const titleKey =
      info.status === "used"
        ? "link.usedTitle"
        : info.status === "expired"
          ? "link.expiredTitle"
          : "link.invalidTitle";
    const bodyKey =
      info.status === "used" || info.status === "expired"
        ? "link.signInBody"
        : "link.invalidBody";
    body = (
      <div className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-500">
          {info.status === "used" || info.status === "expired" ? (
            <KeyRound size={26} strokeWidth={1.75} aria-hidden />
          ) : (
            <Link2Off size={26} strokeWidth={1.75} aria-hidden />
          )}
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">{t(titleKey)}</h1>
          <p className="mt-2 text-sm text-gray-600">{t(bodyKey)}</p>
        </div>
        <button
          type="button"
          onClick={toSignIn}
          className="block w-full rounded-lg bg-pme-blue py-3 text-base font-semibold text-white shadow"
        >
          {t("link.signIn")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-gradient-to-b from-pme-sky via-pme-paper to-pme-paper safe-x">
      <div className="w-full max-w-sm px-6 py-8">
        <div className="mb-8 flex flex-col items-center">
          <BrandLockup stacked size="lg" />
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-card">{body}</div>
      </div>
    </div>
  );
}
